import { lazy, Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import RecoveryPromptScreen from "@/components/RecoveryPromptScreen";
import { rememberHandoffPath } from "@/lib/handoff-memory";

const GuestReelsPage = lazy(() => import("@/pages/GuestReelsPage"));
const GuestSnapsPage = lazy(() => import("@/pages/GuestSnapsPage"));
const SinglePostPage = lazy(() => import("@/pages/SinglePostPage"));
const GuestWeatherPage = lazy(() => import("@/pages/WeatherPage"));
const GuestHelpPage = lazy(() => import("@/pages/HelpPage"));

export default function ProtectedRoute() {
  const { user, loading, pendingRecovery } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!user) {
    if (location.pathname === "/reels" || location.pathname.startsWith("/reel/")) {
      return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-black" />}>
          <GuestReelsPage />
        </Suspense>
      );
    }
    if (location.pathname === "/snap") {
      return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-black" />}>
          <GuestSnapsPage />
        </Suspense>
      );
    }
    // The weather station's data is public, so the weather page is open to everyone: standalone, for the same reason as the
    // guest pages above (the app shell would fire authenticated queries that can only 401 for a visitor).
    if (location.pathname === "/weather") {
      return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center" style={{ background: "#0b1736" }} />}>
          <GuestWeatherPage />
        </Suspense>
      );
    }
    // A shared post link (e.g. https://sksnap.com/post/<id>) has to work for
    // whoever it was sent to, signed in or not - rendered standalone here
    // rather than through <Outlet/>+AppLayout deliberately: AppLayout
    // unconditionally fires useConversations()/useNotifications(), both
    // authenticated-only, which would just be two guaranteed-401 requests
    // (plus a wasted /api/auth/refresh attempt once the first one fails)
    // on every single guest post view for chrome (nav bar, unread badges,
    // email-verification banner) a signed-out visitor has no use for anyway
    // - the exact same reasoning /reels and /snap above already apply via
    // their own guest pages rather than piping guests through the real
    // ones. SinglePostPage itself renders full-screen with its own back
    // button, so it needs no shell around it here.
    if (location.pathname.startsWith("/post/")) {
      // Extracted from the raw path rather than useParams() - a component
      // rendered directly by a parent layout route like this one, instead
      // of through the matched leaf route's own <Outlet/> chain, sits
      // outside where react-router resolves :postId into context; see
      // SinglePostPage's own comment on postIdOverride for the full reasoning.
      const guestPostId = location.pathname.split("/")[2];
      return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-black" />}>
          <SinglePostPage postIdOverride={guestPostId} />
        </Suspense>
      );
    }
    // The Help Center is public too: someone locked out of their account is
    // exactly who needs it. Rendered standalone for the same reason as the
    // post view above (no AppLayout, so no authenticated-only requests).
    if (location.pathname === "/help" || location.pathname.startsWith("/help/")) {
      return (
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-background" role="status" />}>
          <GuestHelpPage standalone />
        </Suspense>
      );
    }
    // A guest's very first impression of the app should be real content,
    // not a login wall - send them to the one page they can actually use
    // rather than /auth.
    if (location.pathname === "/") return <Navigate to="/reels" replace />;
    rememberHandoffPath(location.pathname + location.search);
    return <Navigate to="/auth" replace />;
  }
  if (pendingRecovery) return <RecoveryPromptScreen />;
  return <Outlet />;
}
